import { isNumberUnit } from '@exodus/currency';
import assert from 'minimalistic-assert';
import createAssetClientInterfaceWithCustomSigner from './asset-client-interface.js';
const toNumberUnit = ({ value, currency }) => {
    assert(value, 'value is required');
    assert(currency, 'currency is required');
    if (isNumberUnit(value)) {
        return value;
    }
    return currency.parse(value);
};
/* eslint-disable @exodus/mutable/no-param-reassign-prop-only */
const resolveSendParams = (assetName, asset, options) => {
    const { currency, baseAsset } = asset;
    if (options.receiver.amount) {
        options.receiver.amount = toNumberUnit({
            value: options.receiver.amount,
            currency,
        });
    }
    return {
        ...options,
        ...options.receiver,
        assetName,
        asset,
        baseAsset,
        options: {
            feeAmount: options.fees?.fee,
            ...options,
            ...options.receiver,
            ...options.fees,
        },
    };
};
const createTransactions = ({ assetClientInterface, assetsModule, wallet, walletAccounts, transactionSigner, txWatcher, }) => {
    const createUnsignedTx = (data) => {
        const { asset, walletAccount } = data;
        if (!asset.baseAsset.api?.createUnsignedTx) {
            throw new Error(`Cannot make UnsignedTransaction for '${asset.name}' yet`);
        }
        const unsignedTx = asset.baseAsset.api.createUnsignedTx(data);
        unsignedTx.txMeta = {
            walletAccount,
            accountIndex: walletAccounts.get(walletAccount).index,
            ...unsignedTx.txMeta,
        };
        return unsignedTx;
    };
    const resolveTxId = (broadcastResponse) => {
        if (!broadcastResponse) {
            return null;
        }
        if (typeof broadcastResponse === 'string') {
            return broadcastResponse;
        }
        if (broadcastResponse.txId) {
            return broadcastResponse.txId;
        }
        return null;
    };
    const send = async ({ assetName, ...options }) => {
        const asset = assetsModule.getAsset(assetName);
        const baseAsset = asset.baseAsset;
        if (!baseAsset.api?.sendTx) {
            throw new Error(`Cannot find 'sendTx' function for '${asset.name}'`);
        }
        const sendParams = resolveSendParams(assetName, asset, options);
        const assetClientInterfaceWithPasskeySigner = createAssetClientInterfaceWithCustomSigner({
            assetClientInterface,
            signTransaction: transactionSigner.signTransaction,
        });
        // TODO: remove direct reliance on `wallet`, use `assetClientInterface` for signing instead of passing createUnsignedTx
        // second param dependencies should be installed/injected on plugin construction
        // this file should only provide sendParams, no infrastructure deps
        try {
            return await baseAsset.api.sendTx(sendParams, {
                assetClientInterface: assetClientInterfaceWithPasskeySigner,
                wallet,
                createUnsignedTx,
            });
        }
        catch (error) {
            console.error(`Cannot send tx for asset '${assetName}'. Error ${error.message}`, error);
            throw error;
        }
    };
    async function waitConfirmedTx({ asset, walletAccount, timeout, broadcastResponse, }) {
        const assetName = asset.name;
        const baseAsset = asset.baseAsset;
        const txId = resolveTxId(broadcastResponse); // baseAsset.api.broadcastTx does not have a consistent API.
        if (txId === null) {
            console.warn(`Unable to wait for confirmation for ${baseAsset.name} as broadcast did not return txId`);
            return;
        }
        try {
            await txWatcher.watch({
                assetName,
                txId,
                walletAccount,
                timeout,
            });
        }
        catch (error) {
            if (error.message?.includes('rejected by timeout')) {
                console.warn(`Failed to wait for confirmation for ${baseAsset.name} with error: ${error.message}`, error);
                return; // When timing out, better to keep going. There is no indication the tx may fail in the future.
            }
            throw error; // txWatcher also throws when the tx failed, in this case, let the user know.
        }
    }
    const broadcast = async ({ assetName, signedTx, walletAccount, waitForConfirmation = false, waitForConfirmationTimeout, }) => {
        const asset = assetsModule.getAsset(assetName);
        const baseAsset = asset.baseAsset;
        if (!baseAsset.api?.broadcastTx) {
            throw new Error(`Cannot find 'broadcastTx' function for '${asset.name}'`);
        }
        try {
            const broadcastResponse = await baseAsset.api.broadcastTx(signedTx);
            if (waitForConfirmation) {
                await waitConfirmedTx({
                    broadcastResponse,
                    asset,
                    walletAccount,
                    timeout: waitForConfirmationTimeout,
                });
            }
            return broadcastResponse;
        }
        catch (error) {
            console.error(`Cannot broadcast tx for asset '${assetName}'. Error ${error.message}`, error);
            throw error;
        }
    };
    return {
        send,
        broadcast,
    };
};
const transactionsDefinition = {
    id: 'transactions',
    type: 'module',
    factory: createTransactions,
    dependencies: [
        'assetClientInterface',
        'assetsModule',
        'wallet',
        'walletAccounts',
        'transactionSigner',
        'txWatcher',
    ],
};
export default transactionsDefinition;
